<script setup lang="ts">
import { computed, ref } from "vue";
import { Button, Dialog, IconButton, Input, SegmentedControl, Toast } from "@/design-system";
import type { Plan } from "@/composables/usePlan";
import PlanResult from "./PlanResult.vue";
import PlanTotals from "./PlanTotals.vue";

/** Step 3 -- Plan. Leg switcher, then either the roll-up or a per-leg result. */
const props = defineProps<{ plan: Plan }>();
const emit = defineEmits<{ edit: [] }>();
const p = props.plan;

const tab = ref<string>("total");
const saveOpen = ref(false);
const listOpen = ref(false);

/** FR-3.1: a single-leg race has nothing to switch between. */
const multi = computed(() => p.labels.value.length > 1);

const tabs = computed(() => [
  { value: "total", label: p.t.value.totals },
  ...p.labels.value.map((l) => ({ value: l.key, label: l.name })),
]);

function selectTab(value: string) {
  tab.value = value;
  const found = p.labels.value.find((l) => l.key === value);
  if (found) p.selectLeg(found.index);
}

const showTotals = computed(() => multi.value && tab.value === "total");

function openSave() {
  if (p.isSaved.value) return;
  if (!p.planName.value.trim()) {
    p.planName.value = p.t.value.planNameHint.replace(/^z\. B\. |^e\.g\. /, "");
  }
  saveOpen.value = true;
}

function confirmSave() {
  p.persist();
  saveOpen.value = false;
  window.setTimeout(() => p.clearToast(), 2400);
}

function load(id: string) {
  if (p.loadPlan(id)) {
    listOpen.value = false;
    tab.value = "total";
  }
}
</script>

<template>
  <div class="plan">
    <div class="plan__bar">
      <span
        class="plan__state"
        data-testid="dirty-pill"
      >{{ p.isSaved.value ? p.t.value.saved : p.t.value.unsaved }}</span>
      <span class="plan__bookmark">
        <IconButton
          icon="bookmark"
          variant="outline"
          :label="p.t.value.myPlans"
          data-testid="open-plans"
          @click="listOpen = true"
        />
        <span
          v-if="p.plans.value.length"
          class="plan__count"
        >{{ p.plans.value.length }}</span>
      </span>
    </div>

    <SegmentedControl
      v-if="multi"
      :model-value="tab"
      :options="tabs"
      block
      data-testid="plan-tabs"
      @update:model-value="selectTab"
    />

    <PlanTotals
      v-if="showTotals"
      :plan="p"
    />
    <PlanResult
      v-else
      :plan="p"
    />

    <div class="plan__footer app-actions">
      <Button
        variant="outline"
        size="lg"
        icon="pencil"
        data-testid="edit-inputs"
        @click="emit('edit')"
      >
        {{ p.t.value.editInputs }}
      </Button>
      <Button
        size="lg"
        block
        :icon="p.isSaved.value ? 'check' : 'bookmark'"
        data-testid="save-plan"
        @click="openSave"
      >
        {{ p.isSaved.value ? p.t.value.saved : p.t.value.save }}
      </Button>
    </div>

    <Dialog
      :open="saveOpen"
      :title="p.activeId.value ? p.t.value.updated : p.t.value.save"
      @close="saveOpen = false"
    >
      <div class="plan__fields">
        <Input
          v-model="p.planName.value"
          :label="p.t.value.planName"
          :placeholder="p.t.value.planNameHint"
          data-testid="plan-name"
        />
        <Input
          v-model="p.planDate.value"
          :label="p.t.value.raceDate"
          type="date"
        />
        <p class="plan__note">
          {{ p.t.value.saveNote }}
        </p>
      </div>
      <template #footer>
        <Button
          block
          size="lg"
          :disabled="!p.planName.value.trim()"
          data-testid="confirm-save"
          @click="confirmSave"
        >
          {{ p.t.value.saveCta }}
        </Button>
        <Button
          variant="ghost"
          size="lg"
          @click="saveOpen = false"
        >
          {{ p.t.value.back }}
        </Button>
      </template>
    </Dialog>

    <Dialog
      :open="listOpen"
      :title="p.t.value.savedPlans"
      @close="listOpen = false"
    >
      <p
        v-if="!p.plans.value.length"
        class="plan__note"
        data-testid="no-plans"
      >
        {{ p.t.value.noPlans }}
      </p>
      <ul
        v-else
        class="plan__list"
      >
        <li
          v-for="rec in p.plans.value"
          :key="rec.id"
          class="plan__row"
          :class="{ 'plan__row--active': rec.id === p.activeId.value }"
          :data-plan="rec.id"
        >
          <button
            type="button"
            class="plan__load"
            @click="load(rec.id)"
          >
            <span class="plan__name">{{ rec.name }}</span>
            <span class="plan__date">{{ rec.date || p.t.value.unsaved }}</span>
          </button>
          <span class="plan__figures">
            <span>{{ rec.totalCarbs }} g</span>
            <span class="plan__perhour">{{ rec.perHour }} g/h</span>
          </span>
          <IconButton
            icon="trash-2"
            size="sm"
            :label="p.t.value.del"
            :data-delete="rec.id"
            @click="p.deletePlan(rec.id)"
          />
        </li>
      </ul>
      <template #footer>
        <Button
          variant="outline"
          size="lg"
          block
          @click="p.newPlan(); listOpen = false"
        >
          {{ p.t.value.newPlan }}
        </Button>
      </template>
    </Dialog>

    <div
      v-if="p.toast.value"
      class="plan__toast"
    >
      <Toast
        tone="success"
        data-testid="toast"
      >
        {{ p.toast.value }}
      </Toast>
    </div>
  </div>
</template>

<style scoped>
.plan { display: flex; flex-direction: column; gap: var(--space-6); }
.plan__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-5);
}
.plan__state {
  font-family: var(--font-mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-pill);
  padding: 3px 10px;
}
.plan__bookmark { position: relative; display: flex; }
.plan__count {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border-radius: var(--radius-pill);
  background: var(--clay-500);
  color: var(--white);
  font: var(--weight-bold) 10px/17px var(--font-mono);
  text-align: center;
  pointer-events: none;
}
.plan__footer {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: var(--space-5);
}
.plan__fields { display: flex; flex-direction: column; gap: var(--space-6); }
.plan__note { margin: 0; font: var(--type-body-sm); color: var(--text-muted); }
.plan__list { list-style: none; margin: 0; padding: 0; }
.plan__row {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-5) var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
}
.plan__row--active { background: var(--surface-brand-soft); }
.plan__load {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  border: 0;
  background: none;
  cursor: pointer;
  text-align: left;
}
.plan__name { color: var(--text-strong); }
.plan__date { font: var(--type-body-sm); color: var(--text-muted); }
.plan__figures {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  color: var(--text-strong);
}
.plan__perhour { color: var(--text-muted); font-size: 11px; }
.plan__toast {
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(28px + var(--safe-bottom));
  display: flex;
  justify-content: center;
  z-index: 80;
  pointer-events: none;
}
</style>
